import type { TimeRange } from './availability';

/**
 * Pairwise overlap of two range lists. A boundary the two lists merely touch
 * (one ends exactly where the other starts) is not an overlap — nothing is
 * actually playable at a single instant — so it is excluded rather than
 * emitted as a zero-length range.
 */
export function intersectRanges(a: TimeRange[], b: TimeRange[]): TimeRange[] {
  const as = normalize(a);
  const bs = normalize(b);
  const out: TimeRange[] = [];
  let i = 0;
  let j = 0;
  while (i < as.length && j < bs.length) {
    const start = Math.max(as[i][0], bs[j][0]);
    const end = Math.min(as[i][1], bs[j][1]);
    if (end > start) out.push([start, end]);
    if (as[i][1] < bs[j][1]) i += 1;
    else j += 1;
  }
  return out;
}

function normalize(ranges: TimeRange[]): TimeRange[] {
  return ranges
    .filter(([start, end]) => Number.isFinite(start) && Number.isFinite(end) && end > start)
    .slice()
    .sort((x, y) => x[0] - y[0]);
}

/**
 * UTC millisecond instant to media-timeline seconds, given the anchor UTC at
 * media time 0. `audioUnion`/`videoUnion` are absolute UTC ms; `currentTime`
 * is media seconds — mixing them up seeks to the wrong place silently, since
 * both are just numbers. Never negative: an overlap the anchor arrives after
 * (clock skew) starts playback at the beginning of the timeline instead of
 * seeking negative.
 */
export function utcToMediaSeconds(utcMs: number, anchorUtcMs: number): number {
  return Math.max(0, (utcMs - anchorUtcMs) / 1000);
}
